import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageFirstRun({ navigate }) {
  const link = (id, text) => (
    <button onClick={() => navigate(id)} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">{text}</button>
  );

  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">First Run</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Once the loader has injected MortisEngine into the game, everything happens inside one
        window that is drawn on top of Drakensang Online. This page shows you that window: how to
        open it, how the tabs are organised, and the three things you should check before you start
        farming.
      </p>

      <Callout type="info">
        The bot never switches a feature on by itself: every feature has its own switch, and only
        you flip it. Your settings are written to your own settings file, so you do not have to set
        everything up again the next time you start the game.
      </Callout>

      <DocHeading>Opening the Menu</DocHeading>
      <Step n={1} title="Press INSERT">
        The menu fades in over the game. Press <strong>INSERT</strong> again to hide it &mdash; the
        bot keeps running while the menu is closed.
      </Step>
      <Step n={2} title="Move it where you like">
        Drag the window by its header and resize it from the corner. It remembers its place while
        the game is running.
      </Step>
      <Step n={3} title="Close it while you farm">
        With the menu closed, a small status panel in the corner of the game shows what is switched
        on and what it is doing. You can change its look and corner, or turn it off, on{' '}
        {link('settings', 'SETTINGS')}.
      </Step>
      <Step n={4} title="The game still works around it">
        Only clicks that land <em>on</em> the menu go to the menu. Click next to it and your
        character reacts as usual, and your skill keys keep working while the menu is open. The one
        exception is typing into a text field of the menu: those keystrokes stay in the menu, so your
        character does not run off while you type a name.
      </Step>

      <Callout type="warning">
        If <strong>INSERT</strong> does nothing, the game window may not be focused. Click the game
        once, then press INSERT again. You can move the menu to another key under{' '}
        <strong>SETTINGS &rsaquo; KEYBINDS &rsaquo; Open menu</strong>. That key cannot be cleared:
        if you try, it goes back to INSERT, so you can never lock yourself out of the menu.
      </Callout>

      <DocHeading>The Header</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The strip at the top of the window tells you which build you are running and that the bot
        is loaded into the game.
      </p>
      <DocTable
        headers={['What you see', 'What it means']}
        rows={[
          [<><strong className="text-white">MortisEngine</strong> + version</>, 'The build you are running. Quote this number when you contact support.'],
          [<strong className="text-white">Search settings</strong>, 'Type a word (loot, sell key, cloak…) and it lists every setting that matches, across all tabs. Click one to jump there.'],
          [<span className="text-emerald-400">&#9679; ACTIVE</span>, 'The bot is loaded and running inside the game. If you can see the menu at all, this is what it says.'],
          ['FPS / MS', 'The game’s frame rate and frame time. For support only.'],
        ]}
      />

      <DocHeading>The Tabs</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The row under the header is the main navigation: six tabs, each with its own icon. Most of
        them have a list on the left &mdash; those are the sub-tabs, one per feature. The list stays
        in place when you scroll, so you never have to scroll back up to switch.
      </p>
      <DocTable
        headers={['Tab', 'Sub-tabs', 'What lives there']}
        rows={[
          [<strong className="text-white">AUTOPILOT</strong>, 'Play · Editor (Advanced mode only) · Combat · Key farm · Party · Revive · Potion · Essence', <>Running and building routes, fighting, farming with several characters together, getting back up after a death, potions and essences. Start with {link('autopilot', 'Play')}.</>],
          [<strong className="text-white">FARM</strong>, 'Profiles · Loot · Gather · Sell · Melt runes · Melt gems · Melt items · Open cubes · Bloodshed · Bag · Item lists · Stats', <>Everything that happens to items and currencies: picking up, gathering, selling, melting, bloodshed chests, per-item rules and your farm statistics. See {link('auto-loot', 'Loot')}.</>],
          [<strong className="text-white">QUESTS</strong>, 'Daily challenges · Fortune Hunters', <>Rerolling the game&rsquo;s daily challenges and Fortune Hunters until the one you want comes up (this spends andermant). See {link('quests', 'Quests')}.</>],
          [<strong className="text-white">VISUALS</strong>, 'ESP · Camera · Names', <>What is drawn in the world, the camera past the game&rsquo;s limits, and hiding names. See {link('esp', 'ESP')}.</>],
          [<strong className="text-white">TOOLS</strong>, 'Fast mount', <>Getting on your mount fast. See {link('fast-mount', 'Fast Mount')}. Restarting the game on a timer is done by the loader: see {link('auto-restart', 'Auto Restart')}.</>],
          [<strong className="text-white">SETTINGS</strong>, '—', <>The on-screen status panel, hotkeys, connection, several clients on one PC, data folder, crash reports, unload. See {link('settings', 'Settings')}.</>],
        ]}
      />

      <Callout type="success">
        <strong>A green dot after a tab name</strong> means something on that tab is switched on right
        now. It shows on the main tab (if any of its features is on) and on the sub-tab itself, so you
        can see at a glance what is running without clicking through every tab.
      </Callout>

      {/* UGYANAZ A KEP, mint a Play oldal elso kepe — a leirasa (shoot) ott all. */}
      <Shot
        tab="AUTOPILOT › Play"
        caption="The whole menu: header with the search box, the six main tabs with their icons, and the sub-tab list of AUTOPILOT on the left."
        name="autopilot-01-play-tab.png"
      />

      <DocHeading>Three Things to Check First</DocHeading>
      <Step n={1} title="Your character is in the world">
        The menu already opens on the character screen, but the features need a character standing on
        a map to do anything. Log in first, then start switching things on.
      </Step>
      <Step n={2} title="You are standing in the game, not in a menu">
        Close the game&rsquo;s own inventory, map and shop windows. The bot works with the game, and
        an open game window can swallow what it sends.
      </Step>
      <Step n={3} title="Turn on one feature at a time">
        Start with {link('auto-loot', 'Loot')} &mdash; it is the easiest to see working. Once you
        trust it, add the next one. The {link('features', 'Features Overview')} suggests an order.
      </Step>

      <Callout type="success">
        <strong>The quickest start: a ready-made run.</strong> {link('autopilot', 'AUTOPILOT › Play')}{' '}
        opens with ready-made runs &mdash; Blood chests, Parallel Worlds, Events and World bosses. Pick one,
        check its settings window and press START; you do not have to record a route. Your own routes
        come later, with <strong>Advanced mode</strong>.
      </Callout>

      <DocHeading>Your Settings Are Saved</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Every switch, slider and filter you touch is written to your own settings file
        automatically. You do not have to save anything by hand &mdash; but the{' '}
        <strong className="text-white">ABOUT</strong> card on the SETTINGS tab has a{' '}
        <strong className="text-white">SAVE SETTINGS NOW</strong> button if you want to be sure
        before closing the game.
      </p>
      <Callout type="info">
        Your own routes are saved separately, as named files, and you save those yourself on the{' '}
        <strong>AUTOPILOT &rsaquo; Editor</strong> tab (it shows in Advanced mode). See{' '}
        {link('route-builder', 'Building a Route')}.
        The settings of the Loot, Gather, Sell and Melt tabs (together with your item rules and
        locked bag slots) can also be stored under a name and switched in one go under{' '}
        {link('farm-profiles', 'FARM › Profiles')} &mdash; the on/off switches stay as you left them.
      </Callout>

      <DocHeading>Stopping</DocHeading>
      <DocTable
        headers={['What you want', 'How']}
        rows={[
          ['Stop the run', <>Press <strong>STOP</strong> on the AUTOPILOT &rsaquo; Play tab.</>],
          ['Stop one feature', 'Turn off its switch on its own tab. The rest keeps running — and its green dot goes out.'],
          ['Remove the bot from the game', <>Press <strong>UNLOAD</strong> on the SETTINGS tab and confirm with <strong>YES, UNLOAD</strong>, or press the unload key (<strong>END</strong> unless you changed it under KEYBINDS) with the game window in front. Closing the loader unloads the bot too. To get the bot back you have to inject again.</>],
          ['Close the game', 'Just close it. Nothing is left behind.'],
        ]}
      />
    </div>
  );
}

export default PageFirstRun;
